/** IDEA-08: global Ctrl+K palette — pure matching/ranking (no React, no network). */
export type PaletteKind =
  "action" | "page" | "contact" | "conversation" | "product" | "flow" | "template";

export interface PaletteEntry {
  readonly id: string;
  readonly kind: PaletteKind;
  readonly label: string;
  /** Secondary text (phone, folder, language…), also searched. */
  readonly hint?: string;
  /** Router path, absolute (`/w/:id/...`). */
  readonly to: string;
}

const norm = (s: string): string => s.toLowerCase().normalize("NFKD").replace(/\p{M}/gu, "");

/** 3 = prefix, 2 = word-prefix, 1 = substring, 0 = no match. Arabic works (no case folding needed). */
export function scoreEntry(entry: PaletteEntry, query: string): number {
  const q = norm(query.trim());
  if (q === "") return 1;
  const label = norm(entry.label);
  if (label.startsWith(q)) return 3;
  if (label.split(/\s+/).some((w) => w.startsWith(q))) return 2;
  if (label.includes(q) || norm(entry.hint ?? "").includes(q)) return 1;
  return 0;
}

const KIND_ORDER: Record<PaletteKind, number> = {
  action: 0,
  page: 1,
  contact: 2,
  conversation: 3,
  product: 4,
  flow: 5,
  template: 6,
};

export function rankEntries(
  entries: readonly PaletteEntry[],
  query: string,
  limit = 20,
): PaletteEntry[] {
  return entries
    .map((entry) => ({ entry, score: scoreEntry(entry, query) }))
    .filter((r) => r.score > 0)
    .sort(
      (a, b) =>
        b.score - a.score ||
        KIND_ORDER[a.entry.kind] - KIND_ORDER[b.entry.kind] ||
        a.entry.label.localeCompare(b.entry.label),
    )
    .slice(0, limit)
    .map((r) => r.entry);
}

/** True for Ctrl+K / Cmd+K (also fires inside inputs — it is the app-wide shortcut). */
export function isPaletteShortcut(event: {
  key: string;
  ctrlKey: boolean;
  metaKey: boolean;
  altKey: boolean;
  shiftKey: boolean;
}): boolean {
  return (
    event.key.toLowerCase() === "k" &&
    (event.ctrlKey || event.metaKey) &&
    !event.altKey &&
    !event.shiftKey
  );
}
